<template>
      <div>
            <quote-detail-submenu />

            <core-layout-content medium>
                  <core-layout-headline-and-actions>
                        <template slot="headline">{{ $t("offers") }}</template>
                        <template slot="actions">
                              <j-button
                                    @click="addOffer"
                                    :isLoading="isAddOfferLoading"
                                    v-if="offers.length > 0"
                                    v-can="'writeQuotes'"
                                    action="addOffer"
                              >{{ $t("add_offer") }}</j-button>
                        </template>
                  </core-layout-headline-and-actions>

                  <!-- List of offers -->
                  <offer-item
                        class="mb2"
                        v-for="(offer, i) in offers"
                        :key="offer.id"
                        :name="`${$t('offer')} ${i + 1}`"
                        :policyId="id"
                        :offerId="offer.id"
                        :product="offer.product"
                        :exclusions="offer.exclusions"
                        :pricing="offer.premium"
                        :createdAt="offer.createdAt"
                        :benefitOptions="offer.benefitOptions"
                        :allowDelete="offers.length > 1"
                        @duplicate="addOffer(i)"
                        @discount:created="fetchOffers"
                        @discount:deleted="fetchOffers"
                        @premium:updated="fetchOffers"
                        @deleted="fetchOffers"
                        @loading:created="fetchOffers"
                        @exclusion:created="fetchOffers"
                        @loading:deleted="fetchOffers"
                        @exclusion:deleted="fetchOffers"
                        :data-fup="`component/offerItem${i}`"
                  />

                  <!-- Empty -->
                  <j-card class="bg5 c1 ta2 p2 fs3" v-if="offers.length === 0">{{ $t("errors.no_data") }}</j-card>

                  <!-- Loading toast -->
                  <j-loading-toast v-if="isLoading" />
            </core-layout-content>
      </div>
</template>

<script>
import CrmIcon from "@/components/CrmIcon/"
import JButton from "@/components/JButton/"
import JCard from "@/components/JCard/"
import CoreLayoutContent from "@/modules/core/components/CoreLayoutContent"
import CoreLayoutHeadlineAndActions from "@/modules/core/components/CoreLayoutHeadlineAndActions"
import QuoteDetailSubmenu from "../components/QuoteDetailSubmenu"
import OfferItem from "../components/OfferItem"
import JLoadingToast from "@/components/JLoadingToast/"
import dayjs from "dayjs"
import api from "../api/"
import get from "lodash.get"

/**
 * This component is responsible for fetching offers and it can also add offer
 */
export default {
	components: {
		CrmIcon,
		JButton,
		JCard,
		CoreLayoutContent,
		CoreLayoutHeadlineAndActions,
		QuoteDetailSubmenu,
		OfferItem,
		JLoadingToast
	},

	props: {
		id: { type: String, required: true, default: "" } // From router
	},

	data: () => ({
		isLoading: false,
		isAddOfferLoading: false,
		offers: []
	}),

	computed: {},

	methods: {
		/**
		 * What's happening here is that we take first offer from offers
		 * and use its data to create a new offer
		 * This behaviour might change in the future (perhaps there will be quotation step to create an offer)
		 */
		async addOffer(index = 0) {
			this.isAddOfferLoading = true

			// If no offers, return error
			if (this.offers.length === 0) {
				this.$store.dispatch(
					"showError",
					this.$t("errors.general")
				)
				return
			}

			const offer = get(this, `offers[${index}]`)
			const discountArray = get(
				offer,
				"premium.appliedDiscounts",
				[]
			)
			const discountCodes = discountArray
				? discountArray.map(item => item.code)
				: null
			const benefitOptions = get(offer, "benefitOptions")

			// Prepare offerInput
			const offerInput = {
				productId: get(offer, "product.productId"),
				premium: {
					grossAmount: get(offer, "premium.originalPrice"),
					currencyCode: get(offer, "premium.currencyCode"),
					discountCodes: discountCodes
				},
				benefitOptions: benefitOptions
					? benefitOptions.map(item => {
							return {
								typeId: item.benefitTypeId,
								key: item.key,
								value: item.value
							}
					  })
					: null
			}

			// addOffer call
			const res = await api.addOffer({
				policyId: this.id,
				tenantId: this.$store.state.tenantId,
				offerInput
			})

			// Check errors
			if (res.errors) {
				this.$store.dispatch("showError", res.errors)
				this.isAddOfferLoading = false // remove loading if error
				return
			}

			this.isAddOfferLoading = false
			this.fetchOffers()
		},

		async fetchOffers() {
			this.isLoading = true
			const res = await api.offers({ where: { id: this.id } })
			this.isLoading = false

			if (res.errors) {
				this.$store.dispatch("showError", res.errors)
				return
			}

			// Success
			this.offers = get(res, "data.policies.list[0].offers", [])
		}
	},

	created() {
		this.fetchOffers()
	}
}
</script>
